import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MessageSquare, Phone, Send, X } from 'lucide-react';
import { chatClosing, chatFlow, chatIntro } from '../data/chatFlow';
import { site } from '../data/site';
type Message = {
  id: number;
  from: 'bot' | 'user';
  text: string;
};
type Answers = Record<string, string>;
function fill(text: string, answers: Answers) {
  return text.replace(/\{(\w+)\}/g, (_, key: string) => answers[key] ?? '');
}
export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [started, setStarted] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [typing, setTyping] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [draft, setDraft] = useState('');
  const [done, setDone] = useState(false);
  const timeouts = useRef<number[]>([]);
  const messageId = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const step = chatFlow[stepIndex];
  const awaitingText = !done && !typing && step && !step.options;
  useEffect(() => () => {
    timeouts.current.forEach(id => window.clearTimeout(id));
  }, []);
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [messages, typing]);
  useEffect(() => {
    if (awaitingText) inputRef.current?.focus();
  }, [awaitingText]);
  const pushBot = useCallback((texts: string[], data: Answers) => {
    if (texts.length === 0) return;
    setTyping(true);
    texts.forEach((text, i) => {
      const id = window.setTimeout(() => {
        messageId.current += 1;
        setMessages(prev => [...prev, {
          id: messageId.current,
          from: 'bot',
          text: fill(text, data)
        }]);
        if (i === texts.length - 1) setTyping(false);
      }, 650 * (i + 1));
      timeouts.current.push(id);
    });
  }, []);
  const start = useCallback(() => {
    if (started) return;
    setStarted(true);
    pushBot(chatIntro, {});
  }, [started, pushBot]);
  const submitAnswer = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || !step) return;
    messageId.current += 1;
    setMessages(prev => [...prev, {
      id: messageId.current,
      from: 'user',
      text: trimmed
    }]);
    const nextAnswers = {
      ...answers,
      [step.id]: trimmed
    };
    setAnswers(nextAnswers);
    setDraft('');
    const next = chatFlow[stepIndex + 1];
    if (next) {
      setStepIndex(stepIndex + 1);
      pushBot(next.prompt, nextAnswers);
    } else {
      setStepIndex(stepIndex + 1);
      setDone(true);
      pushBot(chatClosing, nextAnswers);
    }
  };
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) start();
  };
  return <>
      <motion.button type="button" onClick={toggle} aria-expanded={open} aria-label={open ? 'Close chat' : 'Chat with us'} whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.96
    }} className="fixed bottom-20 right-4 z-50 inline-flex h-14 w-14 items-center justify-center rounded-full bg-brass text-navy-900 shadow-[0_18px_40px_-14px_rgba(0,0,0,0.7)] transition-colors hover:bg-brass-light sm:bottom-6 sm:right-6" data-id="element-43" data-mp="1tj94grehxr60">
        <AnimatePresence mode="wait" initial={false} data-id="element-44" data-mp="1tf8rlnjjwj6y">
          {open ? <motion.span key="close" initial={{
          opacity: 0,
          rotate: -90
        }} animate={{
          opacity: 1,
          rotate: 0
        }} exit={{
          opacity: 0,
          rotate: 90
        }} transition={{
          duration: 0.2
        }} data-id="element-45" data-mp="1y5yw6yk5ki0l">
              <X className="h-6 w-6" aria-hidden="true" data-id="element-46" data-mp="1pe5cx4d8ot7b" />
            </motion.span> : <motion.span key="open" initial={{
          opacity: 0,
          rotate: 90
        }} animate={{
          opacity: 1,
          rotate: 0
        }} exit={{
          opacity: 0,
          rotate: -90
        }} transition={{
          duration: 0.2
        }} data-id="element-47" data-mp="1aheg92uy9vle">
              <MessageSquare className="h-6 w-6" aria-hidden="true" data-id="element-48" data-mp="2dslba7v7t8ee" />
            </motion.span>}
        </AnimatePresence>
        {!open && !started && <span className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5" data-id="element-49" data-mp="241qngz2bm0qu">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brass-light opacity-70" data-id="element-50" data-mp="23zmnutbgfnwd" />
            <span className="relative inline-flex h-3.5 w-3.5 rounded-full border-2 border-navy-900 bg-white" data-id="element-51" data-mp="1v1292390lydf" />
          </span>}
      </motion.button>

      <AnimatePresence data-id="element-52" data-mp="1q2hjcxre2x38">
        {open && <motion.section role="dialog" aria-label="Chat with Unbelievable Remodeling & Painting" initial={{
        opacity: 0,
        y: 24,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 16,
        scale: 0.98
      }} transition={{
        duration: 0.32,
        ease: [0.22, 1, 0.36, 1]
      }} className="fixed bottom-36 right-4 z-50 flex h-[min(560px,70vh)] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-2xl bg-white shadow-[0_50px_90px_-40px_rgba(8,19,31,0.85)] ring-1 ring-navy-900/10 sm:bottom-24 sm:right-6" data-id="element-53" data-mp="1l34mdk6t09mx">
            <header className="flex items-center gap-3 bg-navy-900 px-5 py-4" data-id="element-54" data-mp="5kgkcc49cjzb">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brass font-display text-sm font-semibold text-navy-900" data-id="element-55" data-mp="341khsuuyi4yj">
                U
              </span>
              <div className="flex-1" data-id="element-56" data-mp="392gr5ny2ujbc">
                <p className="text-[13px] font-semibold tracking-wide text-white" data-id="element-57" data-mp="3dzgx7yuxfm2g">
                  Unbelievable Remodeling
                </p>
                <p className="flex items-center gap-1.5 text-[11px] text-white/55" data-id="element-58" data-mp="3iwqkb7uufln4">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden="true" data-id="element-59" data-mp="t8zt0sr7yyrx" />
                  Typically replies within an hour
                </p>
              </div>
              <a href={site.phoneHref} aria-label={`Call ${site.phoneDisplay}`} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-white/80 transition-colors hover:border-brass hover:text-brass" data-id="element-60" data-mp="2uafn9z67ipao">
                <Phone className="h-4 w-4" aria-hidden="true" data-id="element-61" data-mp="2zbbrkduh0dgm" />
              </a>
            </header>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-cream px-4 py-5" aria-live="polite" data-id="element-62" data-mp="2blray1lshsai">
              {messages.map(message => <motion.div key={message.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className={`flex ${message.from === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-63" data-mp="2kfnpau8pq0fd">
                  <p className={`max-w-[82%] rounded-2xl px-4 py-3 text-[14px] leading-relaxed ${message.from === 'user' ? 'rounded-br-sm bg-navy-900 text-white' : 'rounded-bl-sm bg-white text-ink-700 ring-1 ring-navy-900/5'}`} data-id="element-64" data-mp="1drzzs69qb8zy">
                    {message.text}
                  </p>
                </motion.div>)}

              {typing && <div className="flex justify-start" data-id="element-65" data-mp="21haw9bk1qh8a">
                  <span className="flex items-center gap-1 rounded-2xl rounded-bl-sm bg-white px-4 py-3.5 ring-1 ring-navy-900/5" data-id="element-66" data-mp="21gspvm3o6mmn">
                    <span className="sr-only" data-id="element-67" data-mp="1mmeb5xr51zds">Typing</span>
                    {[0, 1, 2].map(i => <motion.span key={i} className="h-1.5 w-1.5 rounded-full bg-navy-900/30" animate={{
                opacity: [0.25, 1, 0.25]
              }} transition={{
                duration: 1.1,
                repeat: Infinity,
                delay: i * 0.18
              }} data-id="element-68" data-mp="b4v3nt6iza9f" />)}
                  </span>
                </div>}
            </div>

            <div className="border-t border-navy-900/10 bg-white p-3" data-id="element-69" data-mp="3jk3ufa362pgl">
              {done ? <div className="space-y-2" data-id="element-70" data-mp="3fjjw436uxfsv">
                  <a href="#contact" onClick={() => setOpen(false)} className="block rounded-full bg-brass py-3 text-center text-[11px] font-bold uppercase tracking-[0.16em] text-navy-900 transition-colors hover:bg-brass-light" data-id="element-71" data-mp="3fkmpqivkdwch">
                    Add project details
                  </a>
                  <a href={site.phoneHref} className="flex items-center justify-center gap-2 rounded-full border border-navy-900/15 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-navy-900 transition-colors hover:border-brass" data-id="element-72" data-mp="2bgtykig1qfc">
                    <Phone className="h-4 w-4" aria-hidden="true" data-id="element-73" data-mp="2crhfsafg5a6f" />
                    {site.phoneDisplay}
                  </a>
                </div> : step?.options ? <div className="flex flex-wrap gap-2" data-id="element-74" data-mp="2lqma14jz6hcs">
                  {step.options.map(option => <button key={option} type="button" disabled={typing} onClick={() => submitAnswer(option)} className="rounded-full border border-navy-900/15 px-4 py-2 text-[13px] text-navy-900 transition-colors hover:border-brass hover:bg-brass hover:text-navy-900 disabled:cursor-not-allowed disabled:opacity-40" data-id="element-75" data-mp="2qqh23bfhlczf">
                      {option}
                    </button>)}
                </div> : <form onSubmit={event => {
            event.preventDefault();
            submitAnswer(draft);
          }} className="flex items-center gap-2" data-id="element-76" data-mp="3jew7r7cqaiy3">
                  <label className="sr-only" htmlFor="chat-input" data-id="element-77" data-mp="26thzg7ckseho">
                    {step?.placeholder ?? 'Your message'}
                  </label>
                  <input id="chat-input" ref={inputRef} type={step?.inputType ?? 'text'} value={draft} disabled={typing} onChange={event => setDraft(event.target.value)} placeholder={step?.placeholder ?? 'Type your message'} className="flex-1 rounded-full border border-navy-900/15 px-4 py-3 text-[14px] text-navy-900 placeholder:text-ink-700/40 focus:border-brass focus:outline-none focus:ring-2 focus:ring-brass/25 disabled:opacity-50" data-id="element-78" data-mp="2fkqpmuoh8n52" />
                  <button type="submit" disabled={typing || !draft.trim()} aria-label="Send message" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-navy-900 text-white transition-colors hover:bg-brass hover:text-navy-900 disabled:cursor-not-allowed disabled:opacity-40" data-id="element-79" data-mp="14521w13efk37">
                    <Send className="h-4 w-4" aria-hidden="true" data-id="element-80" data-mp="ivgzm6qhhily" />
                  </button>
                </form>}
            </div>
          </motion.section>}
      </AnimatePresence>
    </>;
}